class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
    this.updateFreeShippingUpsell();
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async updateFreeShippingUpsell() {
    const upsellContainer = document.getElementById('cart-notification-upsell');
    if (!upsellContainer) return;

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();

      if (!cart || !cart.items || cart.items.length === 0) {
        upsellContainer.innerHTML = '';
        return;
      }

      // Check if cart contains a Robe
      const hasRobe = cart.items.some(item => {
        const title = (item.product_title || '').toLowerCase();
        const handle = (item.handle || '').toLowerCase();
        return title.includes('robe') || handle.includes('robe');
      });

      // Check if cart contains an Accessory
      const hasAccessory = cart.items.some(item => {
        const title = (item.product_title || '').toLowerCase();
        const handle = (item.handle || '').toLowerCase();
        return title.includes('towel') || title.includes('headband') || title.includes('bag') || title.includes('shoes') ||
               handle.includes('towel') || handle.includes('headband') || handle.includes('bag') || handle.includes('shoes');
      });

      // Robe + Accessory qualifies for US Free Shipping
      if (hasRobe && hasAccessory) {
        upsellContainer.innerHTML = `
          <div class="cart-fs-banner cart-fs-banner--unlocked">
            <div class="cart-fs-banner__badge">
              <span class="cart-fs-banner__title">US FREE SHIPPING UNLOCKED</span>
            </div>
            <p class="cart-fs-banner__sub">Your robe + accessory receive complimentary US free shipping!</p>
          </div>
        `;
        return;
      }

      // Robe in cart without an accessory: 1-Click Upsell Prompt!
      if (hasRobe && !hasAccessory) {
        const variantId = upsellContainer.getAttribute('data-variant-id');
        const prodTitle = upsellContainer.getAttribute('data-product-title') || 'Skincare Headband';
        const price = upsellContainer.getAttribute('data-price') || '$29';
        const imgUrl = upsellContainer.getAttribute('data-image') || '';

        if (!variantId) {
          upsellContainer.innerHTML = '';
          return;
        }

        // Only US/USD has the $200 free shipping promotion
        const isUSD = (cart.currency === 'USD');
        const thresholdCents = 20000;
        const diffCents = Math.max(0, thresholdCents - cart.total_price);
        const diffFormatted = '$' + Math.ceil(diffCents / 100);

        let headingText = `Add an accessory to complete your set 🎀`;
        let tagText = `+${price} • Complete Your Set`;

        if (isUSD) {
          headingText = diffCents > 0 
            ? `You're <strong>${diffFormatted} away</strong> from US FREE shipping! 🎀` 
            : `Add an accessory to unlock <strong>US FREE SHIPPING</strong> 🎀`;
          tagText = `+${price} • Unlocks US Free Shipping`;
        }

        upsellContainer.innerHTML = `
          <div class="cart-fs-banner cart-fs-banner--prompt">
            <div class="cart-fs-banner__top">
              <span class="cart-fs-tag">COMPLETE THE RITUAL</span>
              <div class="cart-fs-heading">${headingText}</div>
            </div>
            <div class="cart-fs-card">
              ${imgUrl ? `<img src="${imgUrl}" alt="${prodTitle}" width="44" height="48" class="cart-fs-card__img" />` : ''}
              <div class="cart-fs-card__details">
                <span class="cart-fs-card__name">${prodTitle}</span>
                <span class="cart-fs-card__price">${tagText}</span>
              </div>
              <button type="button" class="cart-fs-card__add-btn" id="CartUpsellAddBtn">
                + ADD
              </button>
            </div>
            <a href="/cart" class="cart-fs-dismiss-link" style="display:block; text-align:center; font-size:1.05rem; color:#888; text-decoration:underline; margin-top:0.9rem;">
              No thanks, take me to my cart
            </a>
          </div>
        `;

        const addBtn = document.getElementById('CartUpsellAddBtn');
        if (addBtn) {
          addBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            addBtn.disabled = true;
            addBtn.textContent = '...';

            try {
              const addRes = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                  items: [{ id: parseInt(variantId, 10), quantity: 1 }]
                })
              });
              if (addRes.ok) {
                // Refresh cart notification products
                try {
                  const prodRes = await fetch('/cart?section_id=cart-notification-product');
                  const prodHtml = await prodRes.text();
                  const prodEl = document.getElementById('cart-notification-product');
                  if (prodEl) {
                    const pDoc = new DOMParser().parseFromString(prodHtml, 'text/html');
                    prodEl.innerHTML = pDoc.querySelector('.shopify-section')?.innerHTML || prodHtml;
                  }
                  const bubbleRes = await fetch('/cart?section_id=cart-icon-bubble');
                  const bubbleHtml = await bubbleRes.text();
                  const bubbleEl = document.getElementById('cart-icon-bubble');
                  if (bubbleEl) {
                    const bDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
                    bubbleEl.innerHTML = bDoc.querySelector('.shopify-section')?.innerHTML || bubbleHtml;
                  }
                } catch (e) {}

                await this.updateFreeShippingUpsell();
              } else {
                const addData = await addRes.json();
                throw new Error(addData.description || 'Error adding accessory');
              }
            } catch (err) {
              console.error('Failed to add cart upsell:', err);
              addBtn.disabled = false;
              addBtn.textContent = '+ ADD';
            }
          });
        }
        return;
      }

      // Neither condition met
      upsellContainer.innerHTML = '';
    } catch (err) {
      console.error('Error updating cart notification free shipping:', err);
    }
  }
}

customElements.define('cart-notification', CartNotification);
